import React, { useEffect } from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons'
import { Button, Flex, Text, Icon, HStack, Menu, MenuList } from '@chakra-ui/react'
import useBreakPoint from 'hooks/useBreakPoint'
import { useStores } from 'hooks/useStores'
import isNaN from 'lodash/isNaN'
import { IPagination } from 'components/CkTable'
import DropdownButton from 'components/Dropdown/DropdownButton'
import DropdownSelection from 'components/Dropdown/DropdownSelection'
import { EBreakPoint } from 'constants/theme'
import { ITheme } from 'interfaces/theme'
import {
  DARK_GREY,
  DARK_PAGE_BG,
  DARK_TABLE_BORDER,
  DARK_TEXT_MUTED,
  DARK_TEXT_PRIMARY
} from 'constants/dark-tokens'
import PageSizeDropdown from './components/Dropdown'
import { truncatePagination } from './utils'

export interface ICkPaginationProps {
  pagination: IPagination
  pageSize: number
  setPageSize: (page: number) => void
  showPageSize?: boolean
  pageSizeOptions?: number[]
  pageSizeKey?: string
  /** Opt-in dark surface styling (default light). Set true on dark pages. */
  dark?: boolean
  /**
   * Hide the whole bar when everything fits on one page. Opt-in, and off by default
   * on purpose: the bar also carries the rows-per-page control, so hiding it strands
   * the reader — switch a 12-row list from 10 to 20 rows and the bar disappears with
   * no way back to 10. Callers must not re-implement this test themselves; that is
   * exactly how the trap gets rebuilt per page.
   */
  hideWhenSinglePage?: boolean
}

const CkPagination = (props: ICkPaginationProps) => {
  const { organizationStore } = useStores()
  const { organization } = organizationStore
  const currentTheme: ITheme = organization?.theme ?? {}
  const {
    pagination,
    pageSize = 40,
    setPageSize,
    showPageSize = true,
    pageSizeOptions = [10, 20, 40, 100],
    pageSizeKey = 'pageSize',
    dark = false,
    hideWhenSinglePage = false
  } = props

  // Dark-surface overrides for the nav / page-number buttons. Falls back to the
  // original light styling when `dark` is not set, so other pages are untouched.
  const navButtonProps = dark
    ? { background: 'transparent', borderColor: DARK_GREY, color: DARK_TEXT_PRIMARY, _hover: { background: 'whiteAlpha.100' } }
    : { background: 'gray.50', borderColor: 'gray.200', color: 'gray.800', _hover: { opacity: 0.8 } }
  const { gotoPage, pageIndex, tableLength } = pagination
  const isMobile: boolean = useBreakPoint(EBreakPoint.BASE, EBreakPoint.MD)

  const numberOfPages: number = Math.ceil(tableLength / pageSize)

  const truncatedCkPagination: string[] = truncatePagination(Number(pageIndex), Number(numberOfPages))
  const fitsOnOnePage: boolean = numberOfPages <= 1

  function changePageIndex(page: number): void {
    gotoPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function goPreviousPage(): void {
    const newPage = Number(pageIndex) - 1
    if (newPage >= 1) {
      changePageIndex(newPage)
    }
  }

  function goNextPage(): void {
    const newPage = Number(pageIndex) + 1
    if (newPage <= numberOfPages) {
      changePageIndex(newPage)
    }
  }

  function changePageSize(size: number): void {
    setPageSize(Number(size))
    localStorage.setItem(pageSizeKey, `${size}`)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  useEffect(() => {
    if (window) {
      const pageSizeFromLocalStorage = localStorage.getItem(pageSizeKey)
      if (
        pageSizeFromLocalStorage &&
        !isNaN(Number(pageSizeFromLocalStorage)) &&
        pageSizeOptions.includes(Number(pageSizeFromLocalStorage))
      ) {
        setPageSize(Number(pageSizeFromLocalStorage))
      } else {
        setPageSize(pageSizeOptions[0])
      }
    }
  }, [])

  const SelectPageDropdown = () => (
    <Menu closeOnSelect={true} autoSelect={false} computePositionOnMount>
      {({ isOpen }) => (
        <>
          <DropdownButton
            placeHolder={'Go To'}
            isOpen={isOpen}
            minWidth={{ base: '110px', md: '150px' }}
            width={{ base: '110px', md: '150px' }}
            isTable
            dark={dark}
            fontSize="sm"
          />
          <MenuList
            zIndex="1001"
            minWidth={{ base: '110px', md: '150px' }}
            padding={tableLength === 0 ? '0' : 'none'}
            border={tableLength === 0 ? '0' : 'none'}
            maxHeight="300px"
            overflowY="auto"
            bg={dark ? DARK_PAGE_BG : undefined}
            borderColor={dark ? DARK_TABLE_BORDER : undefined}
          >
            {Array.from({ length: Math.ceil(tableLength / pageSize) }, (_, i) => i + 1).map(option => (
              <DropdownSelection
                key={option}
                onClick={() => changePageIndex(Number(option))}
                width={{ base: '110px', md: '150px' }}
                label={option}
                isSelected={option === pageIndex}
                dark={dark}
              />
            ))}
          </MenuList>
        </>
      )}
    </Menu>
  )

  // Only the opt-in caller drops the bar for a single page. `numberOfPages < 1`
  // still hides it for an empty list, where there is nothing to page or resize.
  if (hideWhenSinglePage && fitsOnOnePage) return null

  return (
    <Flex justifyContent="space-between" hidden={numberOfPages < 1} flexWrap="nowrap" width="full">
      <HStack spacing={2}>
        <Button
          colorScheme="gray"
          variant="outline"
          paddingX={2}
          marginLeft={0}
          disabled={Number(pageIndex) <= 1}
          onClick={goPreviousPage}
          _focus={{ opacity: 1 }}
          {...navButtonProps}
        >
          <Icon width={6} height={6} as={ChevronLeftIcon} />
        </Button>
        {Array.isArray(truncatedCkPagination) &&
          truncatedCkPagination.map((item: string, index: number) => {
            if (isNaN(item)) {
              return (
                <Text key={index} color={dark ? DARK_TEXT_MUTED : undefined}>
                  {item}
                </Text>
              )
            }
            const isActive: boolean = Number(pageIndex) === Number(item)
            if (isMobile && !isActive) {
              return <></>
            }
            const pageButtonStyle = dark
              ? {
                  // Subtle translucent accent for the active page (matches DarkTable's
                  // own pager) instead of a solid bright-blue fill.
                  background: isActive ? 'rgba(46, 143, 204, 0.18)' : 'transparent',
                  border: isActive ? '1px solid rgba(77, 179, 255, 0.45)' : '1px solid transparent',
                  color: isActive ? DARK_TEXT_PRIMARY : DARK_TEXT_MUTED,
                  _hover: { background: isActive ? 'rgba(46, 143, 204, 0.18)' : 'rgba(255, 255, 255, 0.06)' }
                }
              : {
                  background: isActive ? currentTheme?.primaryColor : 'gray.50',
                  border: 0,
                  borderColor: 'gray.200',
                  _hover: { opacity: 0.8 }
                }
            return (
              <Button
                colorScheme={isActive ? 'primary' : 'gray'}
                variant={isActive ? 'primary' : 'outline'}
                {...pageButtonStyle}
                _focus={{ opacity: 1 }}
                padding={2}
                marginRight={2}
                lineHeight={1.5}
                key={`pagination-${index}`}
                onClick={() => (isActive || item === '...' ? {} : changePageIndex(Number(item)))}
                cursor={isActive || item === '...' ? 'default' : 'pointer'}
              >
                {item}
              </Button>
            )
          })}
        <Button
          colorScheme="gray"
          variant="outline"
          paddingX={2}
          marginLeft={0}
          disabled={Number(pageIndex) >= numberOfPages}
          onClick={goNextPage}
          _focus={{ opacity: 1 }}
          {...navButtonProps}
        >
          <Icon width={6} height={6} as={ChevronRightIcon} />
        </Button>
      </HStack>
      {showPageSize && (
        <Flex flexDirection="row" marginLeft={4} display={{ base: 'none', md: 'flex' }}>
          <Text
            marginRight={4}
            alignSelf="center"
            fontWeight={500}
            marginBottom={0}
            color={dark ? DARK_TEXT_MUTED : undefined}
          >
            Show
          </Text>
          <PageSizeDropdown
            item={pageSize}
            options={pageSizeOptions}
            onChange={changePageSize}
            width="150px"
            dark={dark}
          />
        </Flex>
      )}
      {isMobile && <SelectPageDropdown />}
    </Flex>
  )
}

export default CkPagination
